// ============================================================================
// Hookup Service
// Manages store-level hookup types and site-specific hookups
// ============================================================================

import {
  StoreHookupType,
  StoreHookupTypeCreate,
  StoreHookupTypeUpdate,
  SiteHookup,
  SiteHookupCreate,
  SiteHookupUpdate,
} from '../types/site';
import { fetchWithAuth } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

// ============================================================================
// Store Hookup Types
// Uses /store-hookup-types endpoint which auto-filters by authenticated user's store
// ============================================================================

export async function getStoreHookupTypes(activeOnly?: boolean): Promise<StoreHookupType[]> {
  const params = new URLSearchParams();
  if (activeOnly !== undefined) params.append('active_only', activeOnly.toString());
  return fetchWithAuth(`${API_BASE_URL}/store-hookup-types?${params}`);
}

export async function createStoreHookupType(data: StoreHookupTypeCreate): Promise<StoreHookupType> {
  return fetchWithAuth(`${API_BASE_URL}/store-hookup-types`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStoreHookupType(hookupTypeId: number, data: StoreHookupTypeUpdate): Promise<StoreHookupType> {
  return fetchWithAuth(`${API_BASE_URL}/store-hookup-types/${hookupTypeId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteStoreHookupType(hookupTypeId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/store-hookup-types/${hookupTypeId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Site Hookups
// ============================================================================

export async function getSiteHookups(siteId: number): Promise<SiteHookup[]> {
  return fetchWithAuth(`${API_BASE_URL}/hookups/sites/${siteId}/hookups`);
}

export async function addSiteHookup(siteId: number, data: SiteHookupCreate): Promise<SiteHookup> {
  return fetchWithAuth(`${API_BASE_URL}/hookups/sites/${siteId}/hookups`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSiteHookup(hookupId: number, data: SiteHookupUpdate): Promise<SiteHookup> {
  return fetchWithAuth(`${API_BASE_URL}/hookups/site-hookups/${hookupId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSiteHookup(hookupId: number): Promise<void> {
  return fetchWithAuth(`${API_BASE_URL}/hookups/site-hookups/${hookupId}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Named Export Object
// ============================================================================

export const hookupService = {
  getStoreHookupTypes,
  createStoreHookupType,
  updateStoreHookupType,
  deleteStoreHookupType,
  getSiteHookups,
  addSiteHookup,
  updateSiteHookup,
  deleteSiteHookup,
};

export default hookupService;
